import { useEffect, useId, useRef } from 'react'
import type { JSX, ReactNode } from 'react'

/**
 * The modal every dialog and form popup in this console is built on.
 *
 * Extracted from `confirm-destroy.tsx` and `confirm-hide.tsx`, which had the
 * same twenty lines twice and the same two hard-won corrections in the comments
 * (IM-146). Both were found in a real browser and neither is reproducible in
 * jsdom, which is exactly why they belong in one place rather than in every
 * component that opens a dialog.
 *
 * ## Why a native `<dialog>`
 *
 * `showModal` brings focus trapping, Esc, an inert background and the top layer,
 * none of which is worth reimplementing. jsdom 29 has `HTMLDialogElement` but
 * implements neither `showModal` nor `close`, so `web/src/test-setup.ts` stands
 * in for exactly those two — which is also why the two corrections below cannot
 * be covered by a test here.
 *
 * ## `cancel`, never `close`
 *
 * The native `close` event fires for *any* close, including the
 * `element.close()` in this component's own effect cleanup — so wiring it to
 * the dismissal callback makes the dialog dismiss itself, and under StrictMode's
 * effect/cleanup/effect it shuts the instant it opens.
 *
 * ## Focus goes back from an effect, not from the handler
 *
 * Focus cannot be moved out of an open modal, so returning it to the trigger
 * inside the cancel handler silently does nothing and leaves focus on `<body>`.
 * It has to happen after React commits the unmount, which is what the cleanup
 * below does.
 *
 * ## A form is not closed by a stray click (IM-171)
 *
 * A backdrop click dismissed every popup, which is right for a two-sentence
 * confirmation and wrong for the run form, whose whole Terraform document lives
 * in a component the dismissal unmounts — a click a few pixels outside it threw
 * away a new deployment's configuration. So the backdrop dismisses only when
 * `dismissOnBackdrop` says it may, and the default is no: a new form is safe
 * without anybody remembering to make it so.
 *
 * Esc stays a way out, but not a silent one once something has been typed. The
 * dialog marks itself touched on any `input` or `change` event inside it, so no
 * form has to report its own state, and a prefill — which fires neither — leaves
 * an untouched form closing as before. The X asks nothing: it is a deliberate
 * click on the one control labelled Close.
 */
export const Modal = ({
  title,
  onCancel,
  children,
  wide,
  dismissOnBackdrop,
}: {
  readonly title: string
  readonly onCancel: () => void
  readonly children: ReactNode
  /** For a form with more than a couple of fields. See `.modal-wide`. */
  readonly wide?: boolean
  /**
   * A confirmation, where nothing is lost by closing: a click outside the
   * content closes it, and so does Esc without asking. A form leaves it unset.
   */
  readonly dismissOnBackdrop?: boolean
}): JSX.Element => {
  const dialog = useRef<HTMLDialogElement>(null)
  const touched = useRef(false)
  const titleId = useId()

  /**
   * Esc, or the backdrop where allowed. A form asks first once something was
   * typed; a confirmation never does — the destroy dialog's retyped id is not
   * work anybody wants back.
   */
  const dismiss = (): void => {
    const loses = dismissOnBackdrop !== true && touched.current
    if (loses && !window.confirm('Discard what you’ve typed?')) return
    onCancel()
  }

  useEffect(() => {
    const element = dialog.current
    if (element === null) return
    // Whatever opened this, so focus has somewhere to go back to. Read before
    // `showModal` moves it into the dialog.
    const opener = document.activeElement
    element.showModal()
    return () => {
      // A dialog already detached from the document throws on close in some
      // browsers, and React may have removed it before this runs.
      if (element.open) element.close()
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus()
    }
  }, [])

  return (
    <dialog
      className={wide === true ? 'modal modal-wide' : 'modal'}
      ref={dialog}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault()
        dismiss()
      }}
      onInput={() => {
        touched.current = true
      }}
      onChange={() => {
        touched.current = true
      }}
      /*
       * Clicking the backdrop is the one dismissal path a test in this harness
       * can drive: a synthesized Escape does not reach the browser's close
       * watcher, and a bare `<dialog>` created in the page ignores it the same
       * way. The target is the dialog itself only when the click landed outside
       * the content, because everything inside is wrapped.
       */
      onClick={(event) => {
        if (dismissOnBackdrop === true && event.target === dialog.current) dismiss()
      }}
    >
      <div className="modal-body">
        <div className="modal-head">
          <h3 id={titleId}>{title}</h3>
          <button type="button" className="icon-button" aria-label="Close" onClick={onCancel}>
            <CloseIcon />
          </button>
        </div>
        {children}
      </div>
    </dialog>
  )
}

/**
 * The icons this console uses, inline.
 *
 * Inline SVG rather than an icon font or a component library: the console's CSP
 * is `font-src 'self'`, the stylesheet is deliberately framework-free, and four
 * glyphs are not worth a dependency that would have to be reviewed, bundled and
 * kept current. `currentColor` throughout, so an icon inherits whatever the
 * button already decided — including in dark mode, where a hardcoded stroke
 * would be the same failure the literal colours in `styles.css` were.
 *
 * `aria-hidden`, always: every one of these sits inside a control that already
 * carries its own text or an `aria-label`, and a second announcement is noise
 * to a screen reader rather than help.
 */
const icon = (path: JSX.Element): JSX.Element => (
  <svg
    className="icon"
    viewBox="0 0 16 16"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {path}
  </svg>
)

export const PlusIcon = (): JSX.Element => icon(<path d="M8 3.5v9M3.5 8h9" />)

export const CloseIcon = (): JSX.Element => icon(<path d="M4 4l8 8M12 4l-8 8" />)

export const TrashIcon = (): JSX.Element =>
  icon(
    <>
      <path d="M2.75 4.5h10.5" />
      <path d="M6.5 4.5V3.25h3V4.5" />
      <path d="M4 4.5l.6 8.1a.9.9 0 0 0 .9.9h5a.9.9 0 0 0 .9-.9l.6-8.1" />
      <path d="M6.75 7v4M9.25 7v4" />
    </>,
  )

export const RestoreIcon = (): JSX.Element =>
  icon(
    <>
      <path d="M3 8a5 5 0 1 0 1.6-3.7" />
      <path d="M3 2.5V5.5h3" />
    </>,
  )
